import { useEffect, useRef, useState, useCallback } from 'react'
import axios from 'axios'

export const SEARCH_MIN_CHARS = 3
const SEARCH_DELAY_MS = 500
const CACHE_SIZE = 20

// Debounces an expensive search: waits SEARCH_DELAY_MS after the last keystroke,
// ignores queries shorter than SEARCH_MIN_CHARS, skips repeated queries, cancels
// in-flight requests when a newer one starts and caches results per query.
// `fetcher(query, cancelToken)` must return a promise and reject on cancellation.
const useDebouncedSearch = (query, fetcher, { enabled = true } = {}) => {
  const [results, setResults] = useState([])
  const [isLoading, setIsLoading] = useState(false)
  const cache = useRef(new Map())
  const lastQuery = useRef(null)
  const cancelSource = useRef(null)
  const timer = useRef(null)
  const fetcherRef = useRef(fetcher)
  fetcherRef.current = fetcher

  const cancelPending = () => {
    if (cancelSource.current) {
      cancelSource.current.cancel()
      cancelSource.current = null
    }
  }

  const run = useCallback((raw) => {
    const search = (raw || '').trim()
    const q = search.toLowerCase()
    if (!enabled || q.length < SEARCH_MIN_CHARS) {
      cancelPending()
      lastQuery.current = null
      setResults([])
      setIsLoading(false)
      return
    }
    if (q === lastQuery.current) {
      return
    }
    lastQuery.current = q
    cancelPending()
    if (cache.current.has(q)) {
      setResults(cache.current.get(q))
      setIsLoading(false)
      return
    }
    const source = axios.CancelToken.source()
    cancelSource.current = source
    setIsLoading(true)
    fetcherRef.current(search, source.token)
      .then(items => {
        cache.current.set(q, items)
        if (cache.current.size > CACHE_SIZE) {
          cache.current.delete(cache.current.keys().next().value)
        }
        setResults(items)
        setIsLoading(false)
      })
      .catch(error => {
        if (!axios.isCancel(error)) {
          lastQuery.current = null
          setIsLoading(false)
        }
      })
  }, [enabled])

  useEffect(() => {
    clearTimeout(timer.current)
    timer.current = setTimeout(() => run(query), SEARCH_DELAY_MS)
    return () => clearTimeout(timer.current)
  }, [query, run])

  useEffect(() => () => cancelPending(), [])

  // Skip the debounce, e.g. when the user presses Enter
  const searchNow = () => {
    clearTimeout(timer.current)
    run(query)
  }

  return { results, isLoading, searchNow }
}

export default useDebouncedSearch
